
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormLabel } from '@/components/ui/form';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';

interface ColorVariant {
  id: string;
  name: string;
  hex: string;
}

interface ColorVariantsSectionProps {
  colors: ColorVariant[];
  setColors: React.Dispatch<React.SetStateAction<ColorVariant[]>>;
}

const ColorVariantsSection: React.FC<ColorVariantsSectionProps> = ({
  colors,
  setColors,
}) => {
  const [newColor, setNewColor] = useState({ name: '', hex: '#000000' });

  const addColor = () => {
    if (newColor.name.trim() === '') {
      toast.error('Color name is required');
      return;
    }
    
    setColors(prev => [
      ...prev, 
      { 
        id: crypto.randomUUID(), 
        name: newColor.name, 
        hex: newColor.hex 
      }
    ]);
    
    setNewColor({ name: '', hex: '#000000' });
  };

  const removeColor = (id: string) => {
    setColors(prev => prev.filter(color => color.id !== id));
  };

  return (
    <div className="space-y-3 pl-6 border-l-2 border-gray-200">
      <FormLabel>Colors</FormLabel>
      <div className="flex items-center space-x-2">
        <Input
          placeholder="Color name"
          value={newColor.name}
          onChange={(e) => setNewColor({ ...newColor, name: e.target.value })}
          className="flex-1"
        />
        <Input
          type="color"
          value={newColor.hex}
          onChange={(e) => setNewColor({ ...newColor, hex: e.target.value })}
          className="w-14 p-1 h-10"
        />
        <Button type="button" onClick={addColor} size="icon" variant="outline">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      
      {colors.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {colors.map((color) => (
            <div 
              key={color.id} 
              className="flex items-center bg-gray-100 rounded-full pl-2 pr-1 py-1 text-sm"
            >
              <div 
                className="w-4 h-4 rounded-full mr-1" 
                style={{ backgroundColor: color.hex }}
              />
              <span>{color.name}</span>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-5 w-5 ml-1"
                onClick={() => removeColor(color.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ColorVariantsSection;
